import type { ComponentType } from 'react';
import type { MetricTone } from '@lognai/synapse-design-system-v2';
import {
  Boxes,
  CircleAlert,
  DollarSign,
  Package,
  ShieldAlert,
  Truck,
} from '@lognai/synapse-design-system-v2/icons';

export interface WorkspaceKpiDefinition {
  id: string;
  label: string;
  module: string;
  plugin: string;
  permission: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  tone: MetricTone;
  description: string;
  /** Analytics-backed metrics load only when selected. Local metrics reuse page queries. */
  measure?: string;
  visualId?: string;
  unit?: 'count' | 'number' | 'days' | 'percent' | 'currency';
}

export const DEFAULT_WORKSPACE_KPI_IDS = ['orders', 'inventory', 'shipments', 'revenue'] as const;

type KpiEntry = [id: string, label: string, description: string];

function localKpis(
  module: string,
  plugin: string,
  permission: string,
  href: string,
  icon: WorkspaceKpiDefinition['icon'],
  tone: MetricTone,
  entries: KpiEntry[]
): WorkspaceKpiDefinition[] {
  return entries.map(([id, label, description]) => ({
    id,
    label,
    description,
    module,
    plugin,
    permission,
    href,
    icon,
    tone: id === 'revenue' || id === 'average-order-value' ? 'success' : tone,
  }));
}

function analyticsKpis(
  module: string,
  plugin: string,
  permission: string,
  href: string,
  icon: WorkspaceKpiDefinition['icon'],
  tone: MetricTone,
  entries: Array<
    [
      measure: string,
      label: string,
      unit: NonNullable<WorkspaceKpiDefinition['unit']>,
      description: string,
    ]
  >
): WorkspaceKpiDefinition[] {
  return entries.map(([measure, label, unit, description]) => ({
    id: `kpi:${measure.replace('.', ':')}`,
    measure,
    label,
    unit,
    description,
    module,
    plugin,
    permission,
    href,
    icon,
    tone,
  }));
}

export const WORKSPACE_KPI_REGISTRY: readonly WorkspaceKpiDefinition[] = [
  ...localKpis('Orders', 'orders', 'order:sales:read', '/orders/sales', Package, 'primary', [
    ['orders', 'Orders', 'Total sales orders'],
    ['revenue', 'Revenue', 'Total booked sales-order value'],
    ['pending-orders', 'Pending orders', 'Sales orders awaiting processing'],
    ['average-order-value', 'Average order value', 'Average booked sales-order value'],
  ]),
  ...localKpis(
    'Inventory',
    'inventory',
    'inventory:item:read',
    '/inventory/items',
    Boxes,
    'warning',
    [
      ['inventory', 'Inventory', 'Total inventory items'],
      ['stockouts', 'Stocked-out SKUs', 'Items currently out of stock'],
      ['low-stock', 'Below reorder point', 'Items needing replenishment'],
      ['available-units', 'Available units', 'Stock available for allocation'],
      ['reserved-units', 'Reserved units', 'Stock reserved for demand'],
    ]
  ),
  ...localKpis('Shipments', 'shipments', 'shipment:shipment:read', '/shipments', Truck, 'info', [
    ['shipments', 'Active shipments', 'Shipments currently in transit'],
    ['delivered-shipments', 'Delivered shipments', 'Total delivered shipments'],
    ['held-shipments', 'Shipments on hold', 'Shipments currently on hold'],
  ]),
  ...localKpis(
    'Invoices',
    'invoices',
    'invoices:invoice:read',
    '/invoices',
    DollarSign,
    'success',
    [
      ['invoice-approvals', 'Invoices awaiting approval', 'Current invoice approval queue'],
      ['invoice-exceptions', 'Invoice audit exceptions', 'Invoices requiring audit attention'],
      ['unreconciled-payments', 'Unreconciled payments', 'Payments awaiting reconciliation'],
      ['overdue-invoices', 'Overdue invoices', 'Invoices currently past due'],
    ]
  ),
  ...analyticsKpis('Orders', 'orders', 'order:sales:read', '/orders/sales', Package, 'primary', [
    ['order_fulfillment.open_order_count', 'Open sales orders', 'count', 'Current open order book'],
  ]),
  ...analyticsKpis(
    'Backorders',
    'orders',
    'order:backorder:read',
    '/orders/backorders',
    CircleAlert,
    'warning',
    [
      [
        'backorder_exposure.backorder_count',
        'Open backorders',
        'count',
        'Current unfulfilled backorders',
      ],
      [
        'backorder_exposure.remaining_quantity',
        'Backordered units',
        'count',
        'Current outstanding backorder quantity',
      ],
    ]
  ),
  ...analyticsKpis(
    'Purchase Orders',
    'orders',
    'order:purchase:read',
    '/orders/purchase',
    Package,
    'primary',
    [
      [
        'purchase_order_pipeline.purchase_order_count',
        'Purchase orders',
        'count',
        'Current purchasing pipeline',
      ],
      [
        'supplier_delay_risk.average_days_late',
        'Supplier delay',
        'days',
        'Average lateness of current purchase orders',
      ],
    ]
  ),
  ...analyticsKpis(
    'Warehouse',
    'warehouse',
    'warehouse:task:read',
    '/warehouse/tasks',
    Boxes,
    'info',
    [
      [
        'warehouse_operations.open_task_count',
        'Open warehouse tasks',
        'count',
        'Current warehouse task backlog',
      ],
      [
        'warehouse_operations.completed_task_count',
        'Completed warehouse tasks',
        'count',
        'All-time completed warehouse tasks',
      ],
    ]
  ),
  ...analyticsKpis(
    'Shipments',
    'shipments',
    'shipment:shipment:read',
    '/shipments',
    Truck,
    'info',
    [
      [
        'shipment_operations.eta_risk_count',
        'Shipments at ETA risk',
        'count',
        'Current shipments at risk of arriving late',
      ],
      [
        'shipment_performance.on_time_rate',
        'On-time delivery',
        'percent',
        'All eligible delivered shipments',
      ],
      [
        'shipment_performance.average_actual_transit_days',
        'Actual transit time',
        'days',
        'Average across shipments with actual dates',
      ],
      [
        'shipment_milestones.overdue_count',
        'Overdue milestones',
        'count',
        'Current overdue shipment milestones',
      ],
    ]
  ),
  ...analyticsKpis(
    'Procurement',
    'procurement',
    'procurement:rfq:read',
    '/procurement/rfqs',
    Package,
    'primary',
    [
      [
        'procurement_pipeline.open_rfq_count',
        'Open procurement RFQs',
        'count',
        'Current sourcing pipeline',
      ],
      [
        'procurement_pipeline.awarded_rfq_count',
        'Awarded procurement RFQs',
        'count',
        'All-time RFQ awards',
      ],
    ]
  ),
  ...analyticsKpis(
    'Freight RFQ',
    'freight-rfq',
    'freight-rfq:rfq:read',
    '/freight-rfq/rfqs',
    Truck,
    'info',
    [
      [
        'freight_rfq_pipeline.open_rfq_count',
        'Open freight RFQs',
        'count',
        'Current freight sourcing pipeline',
      ],
      [
        'freight_rfq_pipeline.awarded_lane_count',
        'Awarded freight lanes',
        'count',
        'All-time awarded freight lanes',
      ],
    ]
  ),
  ...analyticsKpis(
    'Freight Audit',
    'freight-audit',
    'freight_audit:case:read',
    '/freight-audit',
    ShieldAlert,
    'warning',
    [
      [
        'freight_audit_exposure.open_case_count',
        'Open freight audit cases',
        'count',
        'Current unresolved freight audit cases',
      ],
      [
        'freight_audit_exposure.overdue_case_count',
        'Overdue freight audit cases',
        'count',
        'Current past-due audit cases',
      ],
      [
        'freight_audit_recovery.blocking_finding_count',
        'Blocking freight findings',
        'count',
        'Current findings blocking approval',
      ],
    ]
  ),
  ...analyticsKpis(
    'Demurrage & Detention',
    'dd',
    'shipment:dd:read',
    '/dd',
    CircleAlert,
    'warning',
    [
      [
        'dd_alert_pressure.open_alert_count',
        'Open D&D alerts',
        'count',
        'Current unacknowledged D&D alerts',
      ],
    ]
  ),
  ...analyticsKpis(
    'Tracking',
    'tracking',
    'tracking:subscription:read',
    '/tracking',
    Truck,
    'info',
    [
      [
        'tracking_reliability.active_subscription_count',
        'Active tracking subscriptions',
        'count',
        'Current active tracking subscriptions',
      ],
      [
        'tracking_reliability.stale_subscription_count',
        'Stale tracking',
        'count',
        'Current subscriptions with stale tracking data',
      ],
      [
        'tracking_reliability.errored_subscription_count',
        'Tracking errors',
        'count',
        'Current subscriptions reporting errors',
      ],
    ]
  ),
  ...analyticsKpis('FleetMon', 'fleetmon', 'fleetmon:analytics:read', '/fleetmon', Truck, 'info', [
    [
      'fleet_cost_performance.completed_trip_count',
      'Completed fleet trips',
      'count',
      'All-time completed fleet trips',
    ],
    [
      'fleet_cost_performance.delayed_trip_count',
      'Delayed fleet trips',
      'count',
      'All-time fleet trips with recorded delays',
    ],
    [
      'fleet_cost_performance.delay_minutes',
      'Fleet delay minutes',
      'number',
      'All-time recorded fleet delay minutes',
    ],
  ]),
  ...analyticsKpis(
    'Vendor Onboarding',
    'vendor-onboarding',
    'vendor:application:read',
    '/vendor-onboarding',
    Package,
    'primary',
    [
      [
        'vendor_onboarding_funnel.open_application_count',
        'Open supplier applications',
        'count',
        'Current supplier onboarding queue',
      ],
      [
        'vendor_onboarding_funnel.approved_count',
        'Approved suppliers',
        'count',
        'Approved supplier applications',
      ],
      [
        'vendor_approval_performance.pending_step_count',
        'Pending supplier approvals',
        'count',
        'Current approval steps awaiting decisions',
      ],
    ]
  ),
  ...analyticsKpis('Tasks', 'tasks', 'tasks:task:read', '/tasks', Package, 'primary', [
    ['task_execution.open_task_count', 'Open tasks', 'count', 'Current accessible task backlog'],
    [
      'task_execution.overdue_task_count',
      'Overdue tasks',
      'count',
      'Current accessible overdue tasks',
    ],
  ]),
  ...analyticsKpis(
    'Landed Cost',
    'landed-cost',
    'landed_cost:allocation:read',
    '/landed-cost',
    DollarSign,
    'success',
    [
      [
        'landed_cost_analysis.unverifiable_allocation_count',
        'Unverified landed-cost allocations',
        'count',
        'Allocations missing verification evidence',
      ],
    ]
  ),
  ...analyticsKpis('Products', 'products', 'product:product:read', '/products', Boxes, 'primary', [
    ['product_performance.product_count', 'Products', 'count', 'Current product catalog'],
    [
      'product_supply_planning.average_days_of_supply',
      'Days of supply',
      'days',
      'Average coverage using trailing 90-day demand',
    ],
  ]),
];
